import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { Products } from '../../order/models/product.model';
import { AddEditProductComponent } from '../../order/product/add-edit-product/add-edit-product.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import {
    ShipmentChargeLine,
    ShipmentChargeLineBulkCreateRequest,
    ShipmentChargeProductOption,
    ShipmentChargeProductVariantOption
} from '../models/charge-line/shipment-charge-line';
import { ShipmentChargeType } from '../models/charge-line/shipment-charge-type.enum';
import { ShipmentDelivery } from '../models/delivery/shipment-delivery.model';
import { Shipment } from '../models/shipment';
import { ShipmentService } from '../shipment.service';

type ChargeAllocationScope = 'SHIPMENT' | 'DELIVERIES';
type ChargeSaveRequest = ShipmentChargeLine | ShipmentChargeLineBulkCreateRequest;

interface DeliveryChargePricingRow {
    deliveryId: number;
    deliveryNumber: string;
    buyingChargeAmount?: number;
    sellingChargeAmount?: number;
    buyingAmountEdited: boolean;
    sellingAmountEdited: boolean;
}

@Component({
    selector: 'app-charge-line-dialog',
    templateUrl: './charge-line-dialog.component.html',
    styleUrls: ['./charge-line-dialog.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        SelectModule,
        MultiSelectModule,
        DatePickerModule,
        ButtonModule,
        InputNumberModule,
        TextareaModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        TooltipModule,
        PageFormSectionComponent
    ],
    providers: [DialogService]
})
export class ChargeLineDialogComponent implements OnInit, OnChanges {
    @Input() chargeLineDialogVisible = false;
    @Input() shipment!: Shipment;
    @Input() orderDeliveryList: ShipmentDelivery[] = [];
    @Input() selectedDelivery?: ShipmentDelivery;
    @Input() selectedChargeLine?: ShipmentChargeLine;
    @Input() saveChargeLineInProgress = false;

    @Output() onHide = new EventEmitter<void>();
    @Output() onSave = new EventEmitter<ChargeSaveRequest>();

    chargeLineDialogHeader = '';
    selectedEffectiveDate?: Date;
    submittedChargeLine = false;
    chargeTypeOptions: Array<{ label: string; value: ShipmentChargeType }> = [];
    allocationScopeOptions = [
        { label: 'Shipment level', value: 'SHIPMENT' as ChargeAllocationScope },
        { label: 'Selected deliveries', value: 'DELIVERIES' as ChargeAllocationScope }
    ];
    allocationScope: ChargeAllocationScope = 'SHIPMENT';
    selectedDeliveryIds: number[] = [];
    deliveryChargePricingRows: DeliveryChargePricingRow[] = [];
    selectedCatalogSelection?: string;
    chargeProducts: ShipmentChargeProductOption[] = [];
    loadingChargeProducts = false;
    supplierBuyingAmount?: number;
    internalCostAmount?: number;
    private legacyConversionActive = false;
    private productDialogRef?: DynamicDialogRef;

    constructor(
        private messageService: MessageService,
        private shipmentService: ShipmentService,
        private dialogService: DialogService
    ) {}

    ngOnInit(): void {
        this.chargeTypeOptions = this.getChargeTypeOptions();
        this.syncSelectedDelivery();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedChargeLine']?.currentValue) {
            this.setupDialog();
        }
    }

    onDialogShow(): void {
        this.syncSelectedDelivery();
        if (this.isEnterpriseWorkspace()) {
            this.loadChargeProducts();
        }
    }

    hideChargeLineDialog(): void {
        this.onHide.emit();
    }

    saveChargeLine(): void {
        this.submittedChargeLine = true;
        if (!this.selectedChargeLine || this.isReadOnlyLegacyCharge()) {
            this.showError('Select “Replace with catalog service” before changing this legacy charge.');
            return;
        }
        const bulkDeliveryPricing = this.isBulkDeliveryPricing();
        const effectiveCost = this.effectiveCostAmount();
        if (!this.selectedChargeLine.chargeType || !this.selectedEffectiveDate) {
            this.showError('Please fill all required fields.');
            return;
        }
        if (this.isEnterpriseWorkspace()
            && (!this.selectedChargeLine.productId || !this.selectedChargeLine.variantId)) {
            this.showError('Select a catalog service.');
            return;
        }
        if (bulkDeliveryPricing && this.selectedDeliveryIds.length === 0) {
            this.showError('Select at least one delivery.');
            return;
        }
        if (bulkDeliveryPricing && !this.hasValidDeliveryPricingRows()) {
            this.showError('Enter a cost and customer charge of zero or greater for every selected delivery.');
            return;
        }
        if (!bulkDeliveryPricing && (effectiveCost == null || this.selectedChargeLine.sellingChargeAmount == null)) {
            this.showError('Please fill all required fields.');
            return;
        }
        if (this.isMiscellaneousCharge() && !this.selectedChargeLine.remarks?.trim()) {
            this.showError('Remarks are required for unknown or miscellaneous charges.');
            return;
        }

        const chargeLine: ShipmentChargeLine = {
            ...this.selectedChargeLine,
            buyingChargeAmount: effectiveCost,
            chargeSource: this.isEnterpriseWorkspace() ? 'PRODUCT' : 'MANUAL',
            effectiveDate: this.selectedEffectiveDate.getTime()
        };
        if (!this.isEnterpriseWorkspace()) {
            this.clearProductFields(chargeLine);
        }
        if (bulkDeliveryPricing) {
            chargeLine.chargeLineId = undefined;
            chargeLine.deliveryId = undefined;
            this.onSave.emit({
                chargeLine,
                deliveryCharges: this.deliveryChargePricingRows.map(row => ({
                    deliveryId: row.deliveryId,
                    buyingChargeAmount: row.buyingChargeAmount ?? 0,
                    sellingChargeAmount: row.sellingChargeAmount ?? 0
                }))
            });
            return;
        }
        this.onSave.emit(chargeLine);
    }

    convertLegacyCharge(): void {
        if (!this.isReadOnlyLegacyCharge() || !this.selectedChargeLine) {
            return;
        }
        this.legacyConversionActive = true;
        this.selectedChargeLine.chargeSource = 'PRODUCT';
    }

    onCatalogSelectionChange(preserveAmounts = false): void {
        if (!this.selectedChargeLine || !this.selectedCatalogSelection) {
            return;
        }
        const selection = this.catalogOptions().find(option => option.value === this.selectedCatalogSelection);
        if (!selection) {
            return;
        }
        const product = this.chargeProducts.find(item => item.productId === selection.productId);
        const variant = product?.variants.find(item => item.variantId === selection.variantId);
        if (!product || !variant) {
            return;
        }
        Object.assign(this.selectedChargeLine, {
            chargeSource: 'PRODUCT',
            productId: product.productId,
            variantId: variant.variantId,
            productCode: product.productCode,
            productName: product.productName,
            variantCode: variant.variantCode,
            variantName: variant.variantName,
            chargeCurrency: product.currencyCode,
            taxRate: product.taxRate,
            chargeType: product.defaultChargeType,
            incomeAccountId: product.incomeAccountId,
            incomeAccountName: product.incomeAccountName,
            expenseAccountId: product.expenseAccountId,
            expenseAccountName: product.expenseAccountName
        });
        if (!preserveAmounts) {
            this.selectedChargeLine.buyingChargeAmount = variant.buyingRate;
            this.selectedChargeLine.sellingChargeAmount = variant.sellingRate;
        }
        this.syncCostPresentation();
        this.syncUneditedDeliveryPricingRows();
        this.updateOverrideFlags();
    }

    onAllocationScopeChange(): void {
        this.syncDeliveryPricingRows();
    }

    onDeliverySelectionChange(): void {
        this.syncDeliveryPricingRows();
    }

    onDeliveryBuyingAmountChange(row: DeliveryChargePricingRow, value: number | null): void {
        row.buyingChargeAmount = value ?? undefined;
        row.buyingAmountEdited = true;
    }

    onDeliverySellingAmountChange(row: DeliveryChargePricingRow, value: number | null): void {
        row.sellingChargeAmount = value ?? undefined;
        row.sellingAmountEdited = true;
    }

    copyFirstDeliveryPricingToAll(): void {
        const firstRow = this.deliveryChargePricingRows[0];
        if (!firstRow) {
            return;
        }
        this.deliveryChargePricingRows.slice(1).forEach(row => {
            row.buyingChargeAmount = firstRow.buyingChargeAmount;
            row.sellingChargeAmount = firstRow.sellingChargeAmount;
            row.buyingAmountEdited = true;
            row.sellingAmountEdited = true;
        });
    }

    onSupplierBuyingAmountChange(value: number | null): void {
        this.supplierBuyingAmount = value ?? undefined;
        if (this.selectedChargeLine && !this.isInternalTransport()) {
            this.selectedChargeLine.buyingChargeAmount = this.supplierBuyingAmount;
        }
        this.updateOverrideFlags();
    }

    onInternalCostAmountChange(value: number | null): void {
        this.internalCostAmount = value ?? undefined;
        if (this.selectedChargeLine && this.isInternalTransport()) {
            this.selectedChargeLine.buyingChargeAmount = this.internalCostAmount;
        }
        this.updateOverrideFlags();
    }

    isEnterpriseWorkspace(): boolean {
        return !!this.shipment?.owningCompany?.enableEnterpriseFeatures;
    }

    isExistingCharge(): boolean {
        return !!this.selectedChargeLine?.chargeLineId;
    }

    /** A delivery workspace already supplies the only valid target. */
    isDeliveryScoped(): boolean {
        return !!this.selectedDelivery?.deliveryId;
    }

    isBulkDeliveryPricing(): boolean {
        return !this.isExistingCharge() && !this.isDeliveryScoped()
            && this.allocationScope === 'DELIVERIES';
    }

    costLabel(): string {
        return this.isInternalTransport() ? 'Internal cost' : 'Supplier cost';
    }

    deliveryMargin(row: DeliveryChargePricingRow): number {
        return Number(row.sellingChargeAmount ?? 0) - Number(row.buyingChargeAmount ?? 0);
    }

    isReadOnlyLegacyCharge(): boolean {
        return this.isEnterpriseWorkspace() && this.isExistingCharge()
            && this.selectedChargeLine?.chargeSource !== 'PRODUCT'
            && !this.selectedChargeLine?.productId && !this.legacyConversionActive;
    }

    isInternalTransport(): boolean {
        const source = (this.shipment?.buyingPriceSource || '').trim().toUpperCase();
        if (['INTERNAL_TRANSPORT_COST', 'SISTER_INTERNAL_TRANSPORT_COST',
            'CUSTOM_INTERNAL_TRANSPORT_COST'].includes(source)) {
            return true;
        }
        return ['OWNED', 'SISTER_OWNED'].includes(
            (this.shipment?.resourceOwnershipType || '').trim().toUpperCase());
    }

    updateOverrideFlags(): void {
        if (!this.selectedChargeLine || !this.isEnterpriseWorkspace()) {
            return;
        }
        const variant = this.selectedVariant();
        if (!variant) {
            return;
        }
        this.selectedChargeLine.buyingPriceOverridden =
            Number(this.selectedChargeLine.buyingChargeAmount ?? 0) !== Number(variant.buyingRate ?? 0);
        this.selectedChargeLine.sellingPriceOverridden =
            Number(this.selectedChargeLine.sellingChargeAmount ?? 0) !== Number(variant.sellingRate ?? 0);
    }

    openProductDialog(): void {
        this.productDialogRef?.close();
        this.productDialogRef = this.dialogService.open(AddEditProductComponent, {
            header: 'Add Shipment Charge Service',
            modal: true,
            width: '96vw',
            breakpoints: { '1400px': '96vw', '960px': '98vw', '640px': '100vw' },
            contentStyle: { 'max-height': 'calc(100vh - 7rem)', overflow: 'auto' },
            styleClass: 'shipment-charge-product-dialog',
            data: { presetShipmentCharge: true }
        });
        this.productDialogRef.onClose.subscribe((product?: Products) => {
            this.productDialogRef = undefined;
            if (product?.productID) {
                this.loadChargeProducts(product.productID);
            }
        });
    }

    getAvailableDeliveriesForDropdown(): Array<{ label?: string; value?: number }> {
        return (this.orderDeliveryList || []).map(delivery => ({
            label: delivery.deliveryNumber,
            value: delivery.deliveryId
        }));
    }

    catalogOptions(): Array<{ label: string; value: string; productId: number; variantId: number }> {
        return this.chargeProducts.flatMap(product => product.variants.map(variant => ({
            label: `${product.productName}${product.productCode ? ` (${product.productCode})` : ''} — ${variant.variantName}`,
            value: `${product.productId}:${variant.variantId}`,
            productId: product.productId,
            variantId: variant.variantId
        })));
    }

    selectedProduct(): ShipmentChargeProductOption | undefined {
        return this.chargeProducts.find(product => product.productId === this.selectedChargeLine?.productId);
    }

    selectedVariant(): ShipmentChargeProductVariantOption | undefined {
        return this.selectedProduct()?.variants.find(
            variant => variant.variantId === this.selectedChargeLine?.variantId);
    }

    isMiscellaneousCharge(): boolean {
        return this.selectedChargeLine?.chargeType === ShipmentChargeType.MISCELLANEOUS;
    }

    private setupDialog(): void {
        this.submittedChargeLine = false;
        this.legacyConversionActive = false;
        this.syncSelectedDelivery();
        this.chargeLineDialogHeader = this.isExistingCharge() ? 'Edit Charge Line' : 'Add New Charge Line';
        this.selectedEffectiveDate = this.selectedChargeLine?.effectiveDate
            ? new Date(this.selectedChargeLine.effectiveDate) : new Date();
        this.allocationScope = this.selectedDelivery ? 'DELIVERIES' : 'SHIPMENT';
        this.selectedDeliveryIds = this.selectedDelivery?.deliveryId ? [this.selectedDelivery.deliveryId] : [];
        this.deliveryChargePricingRows = [];
        if (!this.isExistingCharge() && this.selectedChargeLine) {
            this.selectedChargeLine.chargeSource = this.isEnterpriseWorkspace() ? 'PRODUCT' : 'MANUAL';
        }
        this.selectedCatalogSelection = this.selectedChargeLine?.productId && this.selectedChargeLine?.variantId
            ? `${this.selectedChargeLine.productId}:${this.selectedChargeLine.variantId}` : undefined;
        this.syncCostPresentation();
    }

    private syncCostPresentation(): void {
        const storedCost = this.selectedChargeLine?.buyingChargeAmount;
        if (this.isInternalTransport()) {
            this.internalCostAmount = storedCost;
            this.supplierBuyingAmount = undefined;
            return;
        }
        this.supplierBuyingAmount = storedCost;
        this.internalCostAmount = undefined;
    }

    private effectiveCostAmount(): number | undefined {
        return this.isInternalTransport() ? this.internalCostAmount : this.supplierBuyingAmount;
    }

    private syncDeliveryPricingRows(): void {
        if (!this.isBulkDeliveryPricing()) {
            return;
        }
        const deliveryById = new Map((this.orderDeliveryList || [])
            .filter(delivery => delivery.deliveryId != null)
            .map(delivery => [delivery.deliveryId as number, delivery]));
        const existingById = new Map(this.deliveryChargePricingRows.map(row => [row.deliveryId, row]));
        this.deliveryChargePricingRows = this.selectedDeliveryIds
            .map(deliveryId => existingById.get(deliveryId) ?? this.newDeliveryPricingRow(
                deliveryId, deliveryById.get(deliveryId)?.deliveryNumber))
            .filter((row): row is DeliveryChargePricingRow => !!row);
    }

    private newDeliveryPricingRow(deliveryId: number, deliveryNumber?: string): DeliveryChargePricingRow {
        return {
            deliveryId,
            deliveryNumber: deliveryNumber || `Delivery #${deliveryId}`,
            buyingChargeAmount: this.effectiveCostAmount(),
            sellingChargeAmount: this.selectedChargeLine?.sellingChargeAmount,
            buyingAmountEdited: false,
            sellingAmountEdited: false
        };
    }

    private syncUneditedDeliveryPricingRows(): void {
        const defaultCost = this.effectiveCostAmount();
        const defaultSelling = this.selectedChargeLine?.sellingChargeAmount;
        this.deliveryChargePricingRows.forEach(row => {
            if (!row.buyingAmountEdited) {
                row.buyingChargeAmount = defaultCost;
            }
            if (!row.sellingAmountEdited) {
                row.sellingChargeAmount = defaultSelling;
            }
        });
    }

    hasValidDeliveryPricingRows(): boolean {
        return this.deliveryChargePricingRows.length === this.selectedDeliveryIds.length
            && this.deliveryChargePricingRows.every(row => this.isValidNonNegativeAmount(row.buyingChargeAmount)
                && this.isValidNonNegativeAmount(row.sellingChargeAmount));
    }

    private isValidNonNegativeAmount(value?: number): boolean {
        return value != null && Number.isFinite(value) && value >= 0;
    }

    private loadChargeProducts(selectProductId?: number): void {
        const companyId = this.shipment?.owningCompany?.id;
        const currencyCode = this.shipment?.currencyCode;
        if (!companyId || !currencyCode) {
            this.chargeProducts = [];
            this.showError('The shipment company and currency are required to load catalog services.');
            return;
        }
        this.loadingChargeProducts = true;
        this.shipmentService.getChargeProducts(companyId, currencyCode).pipe(
            finalize(() => this.loadingChargeProducts = false)
        ).subscribe({
            next: products => {
                this.chargeProducts = products || [];
                if (selectProductId) {
                    const option = this.catalogOptions().find(item => item.productId === selectProductId);
                    if (option) {
                        this.selectedCatalogSelection = option.value;
                        this.onCatalogSelectionChange();
                    }
                } else if (this.selectedCatalogSelection) {
                    this.onCatalogSelectionChange(true);
                }
            },
            error: () => {
                this.chargeProducts = [];
                this.showError('Catalog services are unavailable. A LogiLink charge cannot be saved until they are available.');
            }
        });
    }

    private clearProductFields(chargeLine: ShipmentChargeLine): void {
        Object.assign(chargeLine, {
            productId: undefined,
            variantId: undefined,
            productCode: undefined,
            productName: undefined,
            variantCode: undefined,
            variantName: undefined,
            taxRate: undefined,
            incomeAccountId: undefined,
            incomeAccountName: undefined,
            expenseAccountId: undefined,
            expenseAccountName: undefined,
            buyingPriceOverridden: false,
            sellingPriceOverridden: false
        });
    }

    private getChargeTypeOptions(): Array<{ label: string; value: ShipmentChargeType }> {
        return Object.values(ShipmentChargeType).map(value => ({
            label: this.chargeTypeLabel(value),
            value
        }));
    }

    private chargeTypeLabel(value: ShipmentChargeType): string {
        if (value === ShipmentChargeType.MISCELLANEOUS) {
            return 'Unknown / Miscellaneous Charge';
        }
        return value.split('_').map(part => part.charAt(0) + part.slice(1).toLowerCase()).join(' ');
    }

    private syncSelectedDelivery(): void {
        const deliveryId = this.selectedDelivery?.deliveryId;
        if (this.selectedChargeLine && deliveryId) {
            this.selectedChargeLine.deliveryId = deliveryId;
            if (!this.isExistingCharge()) {
                this.allocationScope = 'DELIVERIES';
                this.selectedDeliveryIds = [deliveryId];
            }
        }
    }

    private showError(detail: string): void {
        this.messageService.add({ severity: 'error', summary: 'Charge line', detail });
    }
}
